import React from "react";
import { Box, Chip, Stack, Typography, useTheme } from "@mui/material";
import { ToolResultRendererProps } from "./index";

/**
 * Generic renderer for arrays of objects.
 * Displays each item as a card with its properties shown as key-value pairs.
 */

// Get a display-friendly title for an object
function getItemTitle(item: Record<string, unknown>, index: number): string {
  // Try common title fields in order of preference
  const titleFields = [
    "name",
    "title",
    "label",
    "id",
    "dataset_name",
    "file_name",
  ];
  for (const field of titleFields) {
    if (item[field] && typeof item[field] === "string") {
      return item[field] as string;
    }
  }
  return `Item ${index + 1}`;
}

// Get a subtitle/description for an object
function getItemSubtitle(item: Record<string, unknown>): string | null {
  const subtitleFields = ["description", "summary", "type", "status"];
  for (const field of subtitleFields) {
    if (item[field] && typeof item[field] === "string") {
      return item[field] as string;
    }
  }
  return null;
}

// Format a value for display
function formatValue(value: unknown): string {
  if (value === null || value === undefined) return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return value.toLocaleString();
  if (typeof value === "string") return value || "-";
  if (Array.isArray(value))
    return value.length > 0 ? `${value.length} items` : "Empty";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

// Fields to skip in the property grid (shown elsewhere or not useful)
const SKIP_FIELDS = new Set([
  "name",
  "title",
  "label",
  "description",
  "summary", // shown in header
  "__typename",
  "_id", // internal fields
]);

// Fields to show prominently
const PRIORITY_FIELDS = [
  "id",
  "type",
  "status",
  "created_at",
  "updated_at",
  "count",
  "size",
];

interface GenericItemCardProps {
  item: Record<string, unknown>;
  index: number;
}

const GenericItemCard: React.FC<GenericItemCardProps> = ({ item, index }) => {
  const theme = useTheme();
  const title = getItemTitle(item, index);
  const subtitle = getItemSubtitle(item);
  const itemType = typeof item.type === "string" ? item.type : null;

  // Get all displayable properties
  const entries = Object.entries(item)
    .filter(([key]) => !SKIP_FIELDS.has(key))
    .filter(
      ([, value]) => value !== null && value !== undefined && value !== "",
    )
    .sort((a, b) => {
      // Priority fields first
      const aPriority = PRIORITY_FIELDS.indexOf(a[0]);
      const bPriority = PRIORITY_FIELDS.indexOf(b[0]);
      if (aPriority !== -1 && bPriority !== -1) return aPriority - bPriority;
      if (aPriority !== -1) return -1;
      if (bPriority !== -1) return 1;
      return a[0].localeCompare(b[0]);
    })
    .slice(0, 8); // Limit to 8 properties

  return (
    <Box
      sx={{
        borderRadius: "8px",
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: "0 1px 4px rgba(0, 0, 0, 0.06)",
        mb: 1.5,
        "&:last-child": { mb: 0 },
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: theme.palette.grey[50],
          px: 2,
          py: 1.25,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: theme.palette.text.primary }}
          >
            {title}
          </Typography>
          {itemType && (
            <Chip label={itemType} size="small" variant="outlined" />
          )}
        </Stack>
        {subtitle && (
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              display: "block",
              mt: 0.5,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>

      {/* Properties grid */}
      {entries.length > 0 && (
        <Box
          sx={{
            px: 2,
            py: 1.5,
            bgcolor: "#fff",
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "6px 16px",
            fontSize: "0.8125rem",
          }}
        >
          {entries.map(([key, value]) => (
            <Box key={key} sx={{ overflow: "hidden" }}>
              <Typography
                component="span"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: "0.75rem",
                  textTransform: "capitalize",
                }}
              >
                {key.replace(/_/g, " ")}:
              </Typography>{" "}
              <Typography
                component="span"
                sx={{
                  color: theme.palette.text.primary,
                  fontSize: "0.8125rem",
                  wordBreak: "break-word",
                }}
              >
                {formatValue(value)}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export const GenericArrayRenderer: React.FC<ToolResultRendererProps> = ({
  data,
}) => {
  const items = data as Record<string, unknown>[];

  if (!Array.isArray(items) || items.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No results
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1.5 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 1, display: "block" }}
      >
        {items.length} {items.length === 1 ? "item" : "items"}
      </Typography>
      {items.slice(0, 50).map((item, index) => (
        <GenericItemCard key={index} item={item} index={index} />
      ))}
      {items.length > 50 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: "block" }}
        >
          ... and {items.length - 50} more items
        </Typography>
      )}
    </Box>
  );
};
